<template>
  <v-dialog persistent v-model="open" max-width="480px">
    <v-card v-if="selectedSignInMethod === null">
      <v-card-title class="title pt-4 pb-1">
        <h4>How do you want to sign in next time?</h4>
      </v-card-title>

      <v-card-text>
        <v-radio-group v-model="signInMethod" class="sign-in-methods">
          <v-radio
            label="I want to sign in using password"
            value="password"
          ></v-radio>

          <v-radio
            v-if="!smsCodeVerificationDisabled"
            label="Text me a passcode via SMS"
            value="sms"
          ></v-radio>

          <v-radio
            v-if="!emailPasscodeDisabled"
            label="Send me a passcode via Email"
            value="email"
          ></v-radio>
        </v-radio-group>
      </v-card-text>

      <v-card-actions>
        <v-btn
          color="primary"
          class="text-capitalize"
          @click="selectedSignInMethod = signInMethod"
          block
        >
          Next
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-card v-else-if="selectedSignInMethod === 'password'">
      <v-card-title class="title pt-4 pb-1">
        <h4>Set up password</h4>
      </v-card-title>

      <p class="px-3 text-xs-left">
        Please enter new password so that you can sign in next time.
      </p>

      <v-form v-model="newPasswordFormValid" @submit.prevent="submitPassword">
        <v-card-text>
          <div class="text-xs-left mb-1">New password</div>
          <v-text-field
            name="password"
            label="Enter your password"
            id="password"
            :type="showPassword ? 'text' : 'password'"
            :append-icon="showPassword ? 'visibility' : 'visibility_off'"
            @click:append="showPassword = !showPassword"
            v-model="newPassword"
            :rules="passwordRules"
            class="default-input pt-1"
            required
            solo
          />
        </v-card-text>

        <v-card-actions>
          <v-btn
            color="secondary"
            class="text-capitalize"
            @click="selectedSignInMethod = null"
            outline
            block
          >
            Back
          </v-btn>

          <v-btn
            color="primary"
            class="text-capitalize"
            type="submit"
            :disabled="!newPasswordFormValid || submiting"
            :loading="submiting"
            block
          >
            Submit
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>

    <v-card v-else-if="selectedSignInMethod === 'sms'">
      <v-card-title class="title pt-4 pb-1">
        <h4>Sign in via SMS</h4>
      </v-card-title>

      <p class="px-3 text-xs-left">
        Please enter phone number so that you can sign in next time.
      </p>

      <v-form
        v-model="phoneNumberFormValid"
        @submit.prevent="submitPhoneNumber"
      >
        <v-card-text>
          <div class="text-xs-left mb-1">Your phone number</div>
          <v-text-field
            type="text"
            v-model="phoneNumber"
            placeholder="(888) 123-4567"
            mask="phone"
            class="default-input"
            :rules="phoneNumberRules"
            solo
          />

          <div class="text-xs-left mt-3">
            <span
              >* By providing this phone number, you agree to receive messages
              from {{ currentFirm.name }}. Message and data rates may apply.
              Message frequency varies.</span
            >
          </div>
        </v-card-text>

        <v-card-actions>
          <v-btn
            color="secondary"
            class="text-capitalize"
            @click="selectedSignInMethod = null"
            outline
            block
          >
            Back
          </v-btn>

          <v-btn
            color="primary"
            class="text-capitalize"
            type="submit"
            :disabled="!phoneNumberFormValid"
            :loading="submiting"
            block
          >
            Submit
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>

    <v-card
      v-else-if="
        selectedSignInMethod === 'email' ||
          selectedSignInMethod === 'magic-link'
      "
    >
      <v-card-title class="title pt-4 pb-1">
        <h4>Sign in via Email</h4>
      </v-card-title>

      <p v-if="selectedSignInMethod === 'email'" class="px-3 text-xs-left">
        We will send you a <b>passcode</b> to the following email whenever you
        sign in.
      </p>
      <p v-else class="px-3 text-xs-left">
        We will send you a <b>sign-in link</b> to the following email whenever
        you sign in.
      </p>

      <v-form v-model="emailFormValid" @submit.prevent="submitEmail">
        <v-card-text>
          <div class="text-xs-left mb-1">Your email</div>
          <v-text-field
            type="text"
            v-model="email"
            placeholder="Enter your email"
            class="default-input"
            :rules="emailRules"
            solo
          />
        </v-card-text>

        <v-card-actions>
          <v-btn
            color="secondary"
            class="text-capitalize"
            @click="selectedSignInMethod = null"
            outline
            block
          >
            Back
          </v-btn>

          <v-btn
            color="primary"
            class="text-capitalize"
            type="submit"
            :disabled="!emailFormValid"
            :loading="submiting"
            block
          >
            Submit
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import API from '@/api';
import { firebase } from '@firebase/app';
import '@firebase/auth';
import { emailRules } from '@/constants/rules';
import FirmPreferenceMixins from '@/mixins/FirmPreferenceMixins';

export default {
  name: 'SetSignInMethod',
  mixins: [FirmPreferenceMixins],
  data() {
    return {
      open: true,
      signInMethod: null,
      selectedSignInMethod: null,
      showPassword: false,
      newPassword: '',
      phoneNumber: '',
      email: '',
      submiting: false,
      newPasswordFormValid: false,
      passwordRules: [
        v => !!v || 'Password is required',
        v => v.length >= 10 || 'Password must be greater than 10 characters',
        v => {
          const containsUppercase = /[A-Z]/.test(v);
          return (
            containsUppercase ||
            'Password must contains at least one uppercase.'
          );
        },
        v => {
          const containsLowercase = /[a-z]/.test(v);
          return (
            containsLowercase ||
            'Password must contains at least one lowercase.'
          );
        },
        v => {
          const containsNumber = /[0-9]/.test(v);
          return (
            containsNumber || 'Password must contains at least one number.'
          );
        },
        v => {
          const containsSpecial = /[#?!@$%^&*-]/.test(v);
          return (
            containsSpecial ||
            'Password must contains at least one special character (#?!@$%^&*-).'
          );
        },
      ],
      phoneNumberFormValid: false,
      phoneNumberRules: [v => !!v || 'Phone number is required'],
      emailFormValid: false,
      emailRules,
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'user',
    }),
  },
  methods: {
    async submitPassword(e) {
      if (this.newPasswordFormValid) {
        await this.submit(e);
      }
    },

    async submitPhoneNumber(e) {
      if (this.phoneNumberFormValid) {
        await this.submit(e);
      }
    },

    async submitEmail(e) {
      if (this.emailFormValid) {
        await this.submit(e);
      }
    },

    async submit(e) {
      e.preventDefault();

      this.submiting = true;

      try {
        const response = await API.user.setSignInMethod({
          signInMethod: this.selectedSignInMethod,
          newPassword: this.newPassword,
          phoneNumber: this.phoneNumber,
          email: this.email,
        });

        await firebase
          .auth()
          .signInWithCustomToken(response.data.firebase_token);

        const currentUser = await this.$store.dispatch('getCurrentUser');
        const redirectPath = this.$route.query.redirect;

        if (redirectPath && redirectPath !== '/') {
          this.$router.push(redirectPath);
        } else {
          if (currentUser.is_advisor) {
            this.$router.push('/overview');
          } else {
            this.$router.push('/select-entity');
          }
        }
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: e.response
            ? e.response.data.error
            : 'Something went wrong. Our engineering team has been notified about this error.',
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.submiting = false;
      }
    },
  },

  async created() {
    this.phoneNumber = this.currentUser.phone_number;
    this.email =
      this.currentUser.preferences === null ||
      this.currentUser.preferences.has_temp_email
        ? ''
        : this.currentUser.email;
  },
};
</script>

<style scoped lang="postcss">
.v-toolbar {
  background-color: #46535c;
}

.content-container {
  max-width: 400px;
}

.logo {
  height: 40px;
  width: auto;
}

.title {
  justify-content: center;
  text-align: center;
  font-weight: bold;

  & >>> h4 {
    padding: 12px 0;
  }
}

.sign-in-methods {
  & >>> .v-radio {
    padding: 10px;
  }
}

.email-disabled {
  & >>> input {
    color: #222 !important;
  }
}
</style>
